<template>
  <div class="otp-step">
    <a-radio-group
      v-if="allMethods.length > 1"
      v-model:value="method"
      class="otp-methods"
      @change="changeMethod"
    >
      <a-radio-button v-for="m in allMethods" :key="m" :value="m">
        {{ METHOD_LABEL[m] }}
      </a-radio-button>
    </a-radio-group>

    <!-- 보내기 전: 받는 곳(등록이면 입력 안내). 등록 칸보다 위에 둔다 -->
    <p v-if="!sent" class="otp-guide">{{ startText }}</p>

    <!-- 등록 모드: 새 수단(휴대폰 번호·이메일)을 받는다. 바꾸면 받은 인증번호는 버린다 -->
    <a-input
      v-if="enroll"
      ref="target"
      v-model:value="target"
      class="otp-target"
      :type="isPhone ? 'tel' : 'email'"
      :autocomplete="isPhone ? 'tel' : 'email'"
      :maxlength="isPhone ? 13 : 100"
      :placeholder="isPhone ? '휴대폰 번호 (010-0000-0000)' : '이메일 주소'"
      @change="sent && resetCode()"
      @pressEnter="sendCode"
    />

    <!-- 보낸 뒤: 보낸 곳 → [인증번호 칸][작은 다시 받기] 한 줄 -->
    <template v-if="sent">
      <p class="otp-guide">{{ sentText }}</p>
      <div class="otp-row">
        <!-- 숫자만 6자리는 watch code 에서 자른다(붙여넣기 "123-456" 도 들어가게 maxlength 를 걸지 않음) -->
        <a-input
          ref="code"
          v-model:value="code"
          class="otp-code"
          inputmode="numeric"
          autocomplete="one-time-code"
          placeholder="인증번호 6자리"
          @pressEnter="submit"
        />
        <a-button
          class="otp-resend"
          :disabled="cooldown > 0 || verifying"
          :loading="sending"
          @click="sendCode"
        >
          {{ sendLabel }}
        </a-button>
      </div>
    </template>

    <p v-if="error" class="otp-error" role="alert">{{ error }}</p>

    <!-- 주 버튼: 보내기 전엔 「인증번호 받기」(대기 중이면 남은 초), 보낸 뒤엔 확인·로그인·등록 -->
    <a-button
      v-if="sent"
      type="primary"
      class="yellow-button otp-primary"
      block
      :loading="verifying"
      @click="submit"
    >
      {{ submitText }}
    </a-button>
    <a-button
      v-else
      type="primary"
      class="yellow-button otp-primary"
      block
      :loading="sending"
      :disabled="cooldown > 0"
      @click="sendCode"
    >
      {{ sendLabel }}
    </a-button>

    <!-- 부모의 보조 동작(취소·처음부터 다시 로그인) -->
    <slot />

    <p v-if="!enroll" class="otp-help">{{ NO_CODE_HELP }}</p>
  </div>
</template>

<script>
import {
  METHOD_LABEL,
  NO_CODE_HELP,
  enrollTarget,
  errorCode,
  errorMessage,
  isStepUpCancelled,
  otpDigits,
  sendButtonLabel,
  usableMethods,
} from "@/utils/mfa";

// "내 휴대폰 010-****-7892(으)로" 의 수단 이름(D5)
const SHORT_LABEL = { ALIMTALK_OTP: "휴대폰", FALLBACK_EMAIL_OTP: "이메일" };
const TO_LABEL = { ALIMTALK_OTP: "휴대폰으로", FALLBACK_EMAIL_OTP: "이메일로" };

/**
 * 인증번호 입력 단계(수단 선택·요청·6자리 입력·재발송 타이머·남은 횟수·이메일 전환, REQ-MFA-011).
 * 로그인 2단계·첫 로그인 등록(Signin.vue), 본인 확인 창(StepUpModal.vue), 「내 계정 보안」이 같이 쓴다.
 * 서버 호출은 부모가 넘긴 send(method, target?)·verify(method, code) 가 한다.
 * enroll 이면 새 수단 값을 입력받아 send 에 형식을 맞춰 넘긴다(A12·A14).
 * 짜임새(세 앱 공통 D1): 받는 곳 → 보낸 뒤 안내 → [인증번호][다시 받기] → 폭 가득 주 버튼 → 부모의 보조 버튼(slot).
 */
export default {
  name: "OtpStep",
  props: {
    methods: { type: Array, required: true },
    // 수단별로 보낼 곳(가린 값). 예: { ALIMTALK_OTP: "010-****-7892" }
    targets: { type: Object, default: () => ({}) },
    send: { type: Function, required: true },
    verify: { type: Function, required: true },
    submitText: { type: String, default: "확인" },
    enroll: { type: Boolean, default: false },
  },
  data() {
    return {
      METHOD_LABEL,
      NO_CODE_HELP,
      method: this.methods[0],
      target: "",
      // 알림톡 발송 실패 때 서버가 알려 준 대체 수단(REQ-MFA-007)
      fallbackMethods: [],
      sent: false,
      sentTo: "",
      expiresMin: 5,
      code: "",
      cooldown: 0,
      // 429 로 막힌 동안의 안내 — 대기가 끝나면 지운다
      rateLimited: false,
      timer: null,
      error: "",
      sending: false,
      verifying: false,
    };
  },
  computed: {
    allMethods() {
      return [...new Set([...this.methods, ...this.fallbackMethods])];
    },
    isPhone() {
      return this.method === "ALIMTALK_OTP";
    },
    startText() {
      if (this.enroll) {
        return this.isPhone
          ? "등록할 휴대폰 번호를 넣고 카카오 알림톡으로 인증번호를 받으세요."
          : "등록할 이메일 주소를 넣고 인증번호를 받으세요.";
      }
      const target = this.targets[this.method];
      return target
        ? `내 ${SHORT_LABEL[this.method]} ${target}(으)로 인증번호를 보냅니다.`
        : `내 ${TO_LABEL[this.method]} 인증번호를 보냅니다.`;
    },
    sentText() {
      return `${
        this.sentTo || SHORT_LABEL[this.method]
      }(으)로 인증번호를 보냈습니다. ${this.expiresMin}분 안에 입력하세요.`;
    },
    sendLabel() {
      return sendButtonLabel(this.sent, this.cooldown);
    },
  },
  watch: {
    // 숫자만 6자리(붙여넣은 "123-456"·" 123456" 도 6자리로).
    // 화면을 그린 뒤에 고친다 — 앞 값과 같은 6자리로 돌아가면 칸이 다시 그려지지 않아 입력한 글자가 남기 때문
    code: {
      flush: "post",
      handler(value) {
        const digits = otpDigits(value);
        if (digits !== value) this.code = digits;
      },
    },
  },
  mounted() {
    if (this.enroll) this.focus("target");
  },
  beforeUnmount() {
    clearInterval(this.timer);
  },
  methods: {
    focus(ref, select = false) {
      this.$nextTick(() => {
        const input = this.$refs[ref];
        if (!input) return;
        input.focus();
        if (select && input.select) input.select();
      });
    },
    changeMethod() {
      this.target = "";
      this.resetCode();
      if (this.enroll) this.focus("target");
    },
    resetCode() {
      this.sent = false;
      this.code = "";
      this.error = "";
    },
    startCooldown(sec) {
      clearInterval(this.timer);
      this.cooldown = sec;
      this.timer = setInterval(() => {
        this.cooldown -= 1;
        if (this.cooldown > 0) return;
        clearInterval(this.timer);
        if (this.rateLimited) {
          this.rateLimited = false;
          this.error = "";
        }
      }, 1000);
    },
    async sendCode() {
      if (this.cooldown > 0 || this.sending) return;
      const target = this.enroll ? enrollTarget(this.method, this.target) : undefined;
      if (this.enroll && !target) {
        this.error = this.isPhone
          ? "010으로 시작하는 휴대폰 번호 11자리를 입력하세요."
          : "이메일 주소를 확인하세요.";
        this.focus("target");
        return;
      }
      this.sending = true;
      this.error = "";
      try {
        const data = (await this.send(this.method, target)) || {};
        this.sent = true;
        this.code = "";
        this.sentTo = data.targetMasked || this.targets[this.method] || "";
        this.expiresMin = Math.round((data.expiresInSec || 300) / 60);
        this.startCooldown(data.resendAfterSec || 60);
        this.focus("code");
      } catch (error) {
        // 본인 확인 창을 스스로 닫은 것이면 실패 문구를 띄우지 않는다(D5)
        if (isStepUpCancelled(error)) return;
        const data = error?.response?.data || {};
        this.error = errorMessage(
          error,
          "인증번호를 보내지 못했습니다. 잠시 뒤 다시 시도하세요."
        );
        if (data.code === "OTP_RATE_LIMITED" && data.retryAfterSec) {
          // 남은 초는 버튼에서 줄어든다(D3)
          this.rateLimited = true;
          this.startCooldown(data.retryAfterSec);
        }
        if (data.code === "OTP_SEND_FAILED") this.sendFailed(data);
      } finally {
        this.sending = false;
      }
    },
    // 알림톡 실패 → 이메일이 있으면 이메일로 바꿔 둔다(M9). 등록 중이면 입력한 값을 확인하게 한다
    sendFailed(data) {
      if (this.enroll) {
        this.error = `인증번호를 보내지 못했습니다. 입력한 ${
          this.isPhone ? "휴대폰 번호" : "이메일 주소"
        }를 확인하고 잠시 뒤 다시 시도하세요.`;
        return;
      }
      this.fallbackMethods = usableMethods(data.fallbackMethods);
      if (this.isPhone && this.fallbackMethods.includes("FALLBACK_EMAIL_OTP")) {
        this.method = "FALLBACK_EMAIL_OTP";
        this.resetCode();
        this.error =
          "휴대폰으로 인증번호를 보내지 못해 이메일로 바꿨습니다. 「인증번호 받기」를 눌러 이메일로 받으세요.";
      }
    },
    async submit() {
      if (this.verifying) return;
      if (!/^\d{6}$/.test(this.code)) {
        this.error = "인증번호 6자리를 숫자로 입력하세요.";
        this.focus("code");
        return;
      }
      this.verifying = true;
      this.error = "";
      try {
        await this.verify(this.method, this.code);
      } catch (error) {
        if (isStepUpCancelled(error)) return;
        const code = errorCode(error);
        this.error = errorMessage(
          error,
          "인증에 실패했습니다. 잠시 뒤 다시 시도하세요."
        );
        // 만료·5회 오답이면 새 인증번호를 받아야 하므로 입력 칸을 닫는다(오류 문구는 남김)
        if (code === "OTP_EXPIRED") {
          this.sent = false;
          this.code = "";
        } else if (code === "OTP_INVALID") {
          // 틀린 번호는 골라 두어 바로 다시 입력하게
          this.focus("code", true);
        }
      } finally {
        this.verifying = false;
      }
    },
  },
};
</script>

<style scoped>
.otp-step {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.otp-guide {
  margin: 0;
  color: #2f3337;
  font-size: 0.875rem;
  line-height: 1.5;
}
.otp-row {
  display: flex;
  gap: 8px;
}
.otp-code {
  flex: 1;
}
.otp-resend {
  flex: none;
  min-width: 116px;
  height: 40px;
  color: #2f3337;
}

/* 수단 고르기: 줄을 채우는 두 칸(글자 길이만큼 기본 폭, 좁은 로그인 카드에서도 한 줄). 선택된 칸은 앱 주 색(노랑) 바탕에 진한 글자(D8) */
.otp-methods {
  display: flex;
  width: 100%;
}
.otp-methods :deep(.ant-radio-button-wrapper) {
  flex: 1 1 auto;
  padding: 0 12px;
  white-space: nowrap;
  height: 40px;
  line-height: 38px;
  text-align: center;
  font-size: 14px;
  color: #4b5158 !important;
}
.otp-methods :deep(.ant-radio-button-wrapper span) {
  color: inherit !important;
}
.otp-methods :deep(.ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled)) {
  border-color: #ffcc32 !important;
  background: #ffcc32 !important;
  color: #17191c !important;
  font-weight: 700;
}
.otp-methods :deep(.ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled)::before) {
  background-color: #ffcc32 !important;
}
.otp-methods :deep(.ant-radio-button-wrapper:focus-within) {
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.15) !important;
}

.otp-error {
  margin: 0;
  color: #b23a3a;
  font-size: 0.875rem;
  line-height: 1.5;
}
.otp-help {
  margin: 0;
  color: #5c6168;
  font-size: 12px;
  line-height: 1.5;
}
.otp-primary {
  height: 44px;
  font-weight: 700;
}
:deep(.yellow-button) {
  background-color: #ffcc32 !important;
  border-color: #ffcc32 !important;
  color: #17191c !important;
}
:deep(.yellow-button:hover) {
  background-color: #e6b82e !important;
  border-color: #e6b82e !important;
}
:deep(.yellow-button:disabled) {
  background-color: #e8ebed !important;
  border-color: #e8ebed !important;
  color: #4b5158 !important;
}
</style>
